import { Directive, ElementRef, AfterViewInit, OnDestroy, Input } from '@angular/core';
import { debounceTime, map } from 'rxjs/operators';
import { fromEvent } from 'rxjs/observable/fromEvent';
import { Subscription } from 'rxjs/Subscription';

import { DEBOUNCE_TIME, IOS_DELAY } from './sticky-bar.constants';
import { DeviceService } from '../device/device.service';

@Directive({
    selector: '[dclStickyBar]'
})
export class StickyBarDirective implements AfterViewInit, OnDestroy {
    @Input() isBarInModal: boolean;
    @Input() container: string;
    @Input() animationDelay: number;

    private lastScrollTop = 0;
    private topY = 0;
    private height = 0;
    private stickyDebounceScroll: Subscription;
    private stickyDebounceResize: Subscription;
    private isIos: boolean;
    private selector;

    constructor(
        private elem: ElementRef,
        private device: DeviceService
    ) { }

    ngAfterViewInit() {
        this.stickyInit();
    }

    ngOnDestroy() {
        this.elem.nativeElement.remove();
        this.stickyDebounceScroll.unsubscribe();
        this.stickyDebounceResize.unsubscribe();
    }

    /**
     * @description inits sticky directive and events
     */
    stickyInit(): void {
        this.selector = this.container ? document.querySelector(this.container) : document;
        this.isIos = this.device.isIos();
        this.elem.nativeElement.style.transform = 'translateY(0)';
        this.elem.nativeElement.style.width = '100%';

        if (this.isBarInModal && this.isIos) {
            setTimeout(() => {
                document.body.appendChild(this.elem.nativeElement);
            }, this.animationDelay || IOS_DELAY);
        }

        // Fix for iOS scroll issue
        this.stickyDebounce();
    }

    /**
     * @description calculates if element should hide or show with scroll position
     * @param {Object} target
     */
    stickyOnScoll(target): void {
        const scrollTop = target.scrollingElement ?
            target.scrollingElement.scrollTop :
            target.scrollY || target.scrollTop;
        const deltaY = scrollTop - this.lastScrollTop;

        if (deltaY > 0) {
            // Scroll down
            this.topY = this.topY - deltaY;
            this.elem.nativeElement.style.transition = 'none';

            if (!this.height) {
                this.height = this.elem.nativeElement.offsetHeight;
            }

            if (this.topY < -this.height) {
                this.topY = -this.height;
            }
        } else {
            // Scroll up
            this.topY = 0;
            this.elem.nativeElement.style.transition = 'transform 0.2s';
        }

        this.elem.nativeElement.style.transform = `translateY(${this.topY}px)`;
        this.lastScrollTop = scrollTop < 0 ? 0 : scrollTop;
    }

    /**
     * @description prevents scroll function to be called again until DEBOUNCE_TIME passed
     */
    stickyDebounce() {
        this.stickyDebounceScroll = fromEvent(this.selector, 'scroll')
            .pipe(
                debounceTime(DEBOUNCE_TIME),
                map(event => event['target'])
            )
            .subscribe(target => {
                this.stickyOnScoll(target);
            });

        this.stickyDebounceResize = fromEvent(window, 'resize')
            .pipe(
                debounceTime(DEBOUNCE_TIME),
                map(event => event['target'])
            )
            .subscribe(target  => {
                this.stickyOnScoll(target);
            });
    }
}
